import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  AddWishlistCardInput,
  CreateWishlistInput,
  Nullable,
  UpdateWishlistInput,
  UpdateWishlistCardInput,
  UserWithToken,
  WishlistCardEditionsInput,
  Wishlist,
  WishlistCard,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

// Re-export types for convenience
export type {
  Wishlist,
  WishlistCard,
  CreateWishlistInput,
  UpdateWishlistInput,
  UpdateWishlistCardInput,
  AddWishlistCardInput,
  WishlistCardEditionsInput,
};

// =============================================================================
// FRAGMENTS
// =============================================================================

const WISHLIST_CARD_FRAGMENT = gql`
  fragment WishlistCardFields on WishlistCard {
    id
    konamiId
    languages
    minState
    quantity
    card {
      konamiId
      name
      image
    }
    cardSet {
      id
      code
      rarity
      label
      image
      lastCardMarket
      lastCardTrader
      lastTcgPlayer
      set {
        id
        konamiName
        code
      }
    }
    cardSets {
      id
      code
      rarity
      label
      image
      lastCardMarket
      lastCardTrader
      lastTcgPlayer
      set {
        id
        konamiName
        code
      }
    }
    createdAt
  }
`;

const WISHLIST_FRAGMENT = gql`
  fragment WishlistFields on Wishlist {
    id
    name
    description
    shareHash
    isPublic
    cardCount
    createdAt
    updatedAt
  }
`;

const WISHLIST_WITH_CARDS_FRAGMENT = gql`
  ${WISHLIST_CARD_FRAGMENT}
  fragment WishlistWithCardsFields on Wishlist {
    id
    name
    description
    shareHash
    isPublic
    cardCount
    cards {
      ...WishlistCardFields
    }
    createdAt
    updatedAt
  }
`;

const SHARED_WISHLIST_FRAGMENT = gql`
  ${WISHLIST_CARD_FRAGMENT}
  fragment SharedWishlistFields on SharedWishlist {
    id
    name
    description
    cardCount
    cards {
      ...WishlistCardFields
    }
    user {
      id
      name
      picture
    }
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

export const getMyWishlists = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<{
    myWishlists: {
      data: Wishlist[];
      meta: { total: number; perPage: number; lastPage: number; page: number };
    };
  }>(
    gql`
      ${WISHLIST_FRAGMENT}
      query myWishlists {
        myWishlists {
          data {
            ...WishlistFields
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `
  );

export const getWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<{ wishlist: Wishlist | null }>(
    gql`
      ${WISHLIST_WITH_CARDS_FRAGMENT}
      query wishlist($id: Int!) {
        wishlist(id: $id) {
          ...WishlistWithCardsFields
        }
      }
    `,
    variables
  );

export const getMyWantedKonamiIds = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<{ myWantedKonamiIds: number[] }>(
    gql`
      query myWantedKonamiIds {
        myWantedKonamiIds
      }
    `
  );

export const getMyWantedCardSetIds = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<{ myWantedCardSetIds: number[] }>(
    gql`
      query myWantedCardSetIds {
        myWantedCardSetIds
      }
    `
  );

// =============================================================================
// MUTATIONS
// =============================================================================

export const createWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateWishlistInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Wishlist>>(
    gql`
      ${WISHLIST_FRAGMENT}
      mutation createWishlist($input: CreateWishlistInput!) {
        data: createWishlist(input: $input) {
          ...WishlistFields
        }
      }
    `,
    variables
  );

export const updateWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { id: number; input: UpdateWishlistInput }
) =>
  getGraphQLQueryClient(user).request<WithData<Wishlist>>(
    gql`
      ${WISHLIST_FRAGMENT}
      mutation updateWishlist($id: Int!, $input: UpdateWishlistInput!) {
        data: updateWishlist(id: $id, input: $input) {
          ...WishlistFields
        }
      }
    `,
    variables
  );

export const deleteWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteWishlist($id: Int!) {
        data: deleteWishlist(id: $id)
      }
    `,
    variables
  );

export const assignWishlistCardEditions = async (
  user: Nullable<UserWithToken>,
  variables: { wishlistId: number; input: WishlistCardEditionsInput[] }
) =>
  getGraphQLQueryClient(user).request<{ assignWishlistCardEditions: number }>(
    gql`
      mutation assignWishlistCardEditions(
        $wishlistId: Int!
        $input: [WishlistCardEditionsInput!]!
      ) {
        assignWishlistCardEditions(wishlistId: $wishlistId, input: $input)
      }
    `,
    variables
  );

export const addCardToWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { input: AddWishlistCardInput }
) =>
  getGraphQLQueryClient(user).request<WithData<WishlistCard>>(
    gql`
      ${WISHLIST_CARD_FRAGMENT}
      mutation addCardToWishlist($input: AddWishlistCardInput!) {
        data: addCardToWishlist(input: $input) {
          ...WishlistCardFields
        }
      }
    `,
    variables
  );

export const removeCardFromWishlist = async (
  user: Nullable<UserWithToken>,
  variables: { cardId: number }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation removeCardFromWishlist($cardId: Int!) {
        data: removeCardFromWishlist(cardId: $cardId)
      }
    `,
    variables
  );

export const updateWishlistCard = async (
  user: Nullable<UserWithToken>,
  variables: { cardId: number; input: UpdateWishlistCardInput }
) =>
  getGraphQLQueryClient(user).request<WithData<WishlistCard>>(
    gql`
      ${WISHLIST_CARD_FRAGMENT}
      mutation updateWishlistCard($cardId: Int!, $input: UpdateWishlistCardInput!) {
        data: updateWishlistCard(cardId: $cardId, input: $input) {
          ...WishlistCardFields
        }
      }
    `,
    variables
  );

// Quick toggle - returns true if card is now wanted, false if removed
export const toggleWantedCard = async (
  user: Nullable<UserWithToken>,
  variables: { konamiId: number }
) =>
  getGraphQLQueryClient(user).request<{ toggleWantedCard: boolean }>(
    gql`
      mutation toggleWantedCard($konamiId: Int!) {
        toggleWantedCard(konamiId: $konamiId)
      }
    `,
    variables
  );

// Toggle public sharing - returns shareHash if now public, null if private
export const toggleWishlistShare = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<{ toggleWishlistShare: string | null }>(
    gql`
      mutation toggleWishlistShare($id: Int!) {
        toggleWishlistShare(id: $id)
      }
    `,
    variables
  );

// =============================================================================
// PUBLIC QUERIES (no auth required)
// =============================================================================

export type SharedWishlist = {
  id: number;
  name: string;
  description?: string;
  cardCount: number;
  cards: WishlistCard[];
  user: {
    id: number;
    name: string;
    picture?: string;
  };
};

// Get shared wishlist by hash (public, no auth)
export const getSharedWishlist = async (variables: { hash: string }) =>
  getGraphQLQueryClient(null).request<{ sharedWishlist: SharedWishlist | null }>(
    gql`
      ${SHARED_WISHLIST_FRAGMENT}
      query sharedWishlist($hash: String!) {
        sharedWishlist(hash: $hash) {
          ...SharedWishlistFields
        }
      }
    `,
    variables
  );
